// src/components/common/RouteErrorBoundary.jsx
//
// React.lazy() route chunks can fail to load — most commonly right after a
// deploy, when a tab that's been open references a chunk hash the server no
// longer serves. Suspense alone only covers the "still loading" state; a
// failed load throws during render, and without an error boundary that
// throw unmounts the whole app with nothing shown (no error, no fallback —
// just a blank page). This boundary catches that and offers a reload
// instead of a silent blank screen.
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Route failed to load:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div>
            <p className="text-lg font-medium">Something didn't load correctly</p>
            <p className="text-sm text-muted-foreground mt-1">
              This can happen after an app update. Reloading the page usually fixes it.
            </p>
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
